import React, { useMemo } from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../../../constants/forms";
import { getFieldProps } from "../../../../../utils/form-fields";
import { hexToRGBA } from "../../../../../utils/theme";

import TextInput from "../../../controls/TextInput";
import MaskInput from "../../../controls/MaskInput";
import Switch from "../../../controls/Switch";
import Select from "../../../controls/Select";

const getYearOptions = () => {
  const startYear = new Date().getFullYear() - 1900;

  return Array.from(new Array(startYear + 2), (_, i) => {
    return { value: 1900 + i, title: `${1900 + i}` };
  });
}

const StyledContainer = styled.div`
  margin-bottom: 24px;
  border: 1px solid ${hexToRGBA('#000000', 0.1)};
`;

const StyledRow = styled(Row)`
  padding: 16px;
`;

function GeneralQuestionsBlock({ values = {}, onChange, onMouseEnter, onMouseLeave }) {
  const showPaymentFields = ["EIN_SoleProp", "EIN_LLC", "EIN_CCorp", "EIN_SCorp"].includes(values?.code);
  const showExtraFields = values?.code !== "EIN_Trust";
  const showPreviousEINFields = values[FORM_FIELD_KEYS.questionsAppliedBefore] === "true";
  const showHireFields = values[FORM_FIELD_KEYS.questionsHire] === "true";
  const showAnnualTaxesField = values[FORM_FIELD_KEYS.payLessThan4k] === "true";
  const showNumberOfHouseholdEmployeesField = values?.code !== "EIN_Partnership" || values?.code !== "EIN_PSC";
  
  const isNumberOfEmployeesInvalid = useMemo(() => {
    return (
      values[FORM_FIELD_KEYS.numberOfAgricultureEmployees] + 
      values[FORM_FIELD_KEYS.numberOfHouseholdeEmployees] + 
      values[FORM_FIELD_KEYS.numberOfOtherEmployees]
    ) < 1;
  }, [values]);

  return (
    <StyledContainer>
      <div className="inputs-block-title">
        <span>General questions</span>
      </div>
      <StyledRow>
        {showPaymentFields && (
          <>
            <Col xs={12}>
              <Switch
                {...getFieldProps(FORM_FIELD_KEYS.acceptCreditCardPayments)}
                value={values[FORM_FIELD_KEYS.acceptCreditCardPayments]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <Switch
                {...getFieldProps(FORM_FIELD_KEYS.payEmployeesViaPayroll)}
                value={values[FORM_FIELD_KEYS.payEmployeesViaPayroll]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
          </>
        )}
        {showExtraFields && (
          <>
            <Col xs={12}>
              <Switch
                {...getFieldProps(FORM_FIELD_KEYS.questionsHighway)}
                value={values[FORM_FIELD_KEYS.questionsHighway]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <Switch
                {...getFieldProps(FORM_FIELD_KEYS.questionsGambling)}
                value={values[FORM_FIELD_KEYS.questionsGambling]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <Switch
                {...getFieldProps(FORM_FIELD_KEYS.questionsATF)}
                value={values[FORM_FIELD_KEYS.questionsATF]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <Switch
                {...getFieldProps(FORM_FIELD_KEYS.questionsExcise)}
                value={values[FORM_FIELD_KEYS.questionsExcise]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
          </>
        )}
        <Col xs={12}>
          <Switch
            {...getFieldProps(FORM_FIELD_KEYS.questionsAppliedBefore)}
            value={values[FORM_FIELD_KEYS.questionsAppliedBefore]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        {showPreviousEINFields && (
          <>
            <Col xs={12}>
              <MaskInput
                {...getFieldProps(FORM_FIELD_KEYS.previousEINNumberFirst2)}
                value={values[FORM_FIELD_KEYS.previousEINNumberFirst2]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <MaskInput
                {...getFieldProps(FORM_FIELD_KEYS.previousEINNumberLast7)}
                value={values[FORM_FIELD_KEYS.previousEINNumberLast7]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
          </>
        )}
        <Col xs={12}>
          <Switch
            {...getFieldProps(FORM_FIELD_KEYS.questionsHire)}
            value={values[FORM_FIELD_KEYS.questionsHire]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            hideDivider={!showHireFields}
          />
        </Col>
        {showHireFields && (
          <>
            <Col xs={12}>
              <Switch
                {...getFieldProps(FORM_FIELD_KEYS.payLessThan4k)}
                value={values[FORM_FIELD_KEYS.payLessThan4k]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            {showAnnualTaxesField && (
              <Col xs={12}>
                <Switch
                  {...getFieldProps(FORM_FIELD_KEYS.annualTaxes)}
                  value={values[FORM_FIELD_KEYS.annualTaxes]}
                  onChange={onChange}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
              </Col>
            )}
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.numberOfAgricultureEmployees)}
                isInvalid={isNumberOfEmployeesInvalid}
                value={values[FORM_FIELD_KEYS.numberOfAgricultureEmployees]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            {showNumberOfHouseholdEmployeesField && (
              <Col xs={12}>
                <TextInput
                  {...getFieldProps(FORM_FIELD_KEYS.numberOfHouseholdeEmployees)}
                  isInvalid={isNumberOfEmployeesInvalid}
                  value={values[FORM_FIELD_KEYS.numberOfHouseholdeEmployees]}
                  onChange={onChange}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
              </Col>
            )}
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.numberOfOtherEmployees)}
                isInvalid={isNumberOfEmployeesInvalid}
                value={values[FORM_FIELD_KEYS.numberOfOtherEmployees]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <Select
                {...getFieldProps(FORM_FIELD_KEYS.firstDateWagesMonth)}
                value={values[FORM_FIELD_KEYS.firstDateWagesMonth]}
                validated={values.validated}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <Select
                {...getFieldProps(FORM_FIELD_KEYS.firstDateWagesDay)}
                value={values[FORM_FIELD_KEYS.firstDateWagesDay]}
                validated={values.validated}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <Select
                {...getFieldProps(FORM_FIELD_KEYS.firstDateWagesYear)}
                options={getYearOptions()}
                value={values[FORM_FIELD_KEYS.firstDateWagesYear]}
                validated={values.validated}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                hideDivider
              />
            </Col>
          </>
        )}
      </StyledRow>
    </StyledContainer>
  );
}

export default GeneralQuestionsBlock;
